import OperatorApiRequest from "~/operatorApplication/ts/request/OperatorApiRequest";
import Dictionary from "~/ts/library/Dictionary";
import md5 from "~/ts/library/md5";
import {OperatorApplicationConfigInstance} from "~/operatorApplication/ts/service/OperatorApplicationConfig";
import prepareTextForSearch from "~/operatorApplication/ts/service/dialog/prepareTextForSearch";
import {toggleArrayElement} from "~/ts/library/ToggleArrayElement";

const STRICT_SEARCH = "strict";
const SPLIT_SEARCH = "split";
const SEARCH_TYPES: SearchType[] = [STRICT_SEARCH, SPLIT_SEARCH];
type SearchType = typeof STRICT_SEARCH | typeof SPLIT_SEARCH;

export default class OperatorApplicationAutomaticTemplates {
    private static templates: Dictionary<OperatorApplicationAutomaticTemplateInterface> = {};

    public static async fetchFromServer() {
        let result = await (new OperatorApiRequest({
            url: "/messageTemplate/getAutoTemplates",
            silent: true,
            silentError: true
        })).send()
        if (result.isSuccess) {
            this.templates = {};
            let templates = (result.data as OperatorApplicationApi.OperatorMessageTemplateGetAutoTemplatesResultOpenApiModel).templates;
            for (let template of templates) {
                this.addTemplate(template.siteIds, template.text, template.count);
            }
        }
    }

    public static addTemplate(siteIds: string[], text: string, count: number = 1) {
        let key = md5(text);
        let template = this.templates[key];
        if (template) {
            template.count += count;
            for (let siteId of siteIds) {
                toggleArrayElement(template.siteIds, siteId, true);
            }
        } else {
            this.templates[key] = {
                count,
                text,
                search: prepareTextForSearch(text),
                siteIds
            }
        }
    }

    public static search<T>(
        query: string,
        elements: T[],
        getText: (element: T) => string,
        order?: (a: T, b: T) => 1 | -1 | 0,
        max: number = 10,
        reverse: boolean = true
    ): T[] {
        let result = new Map<T, { searchType: SearchType, template: T }>();
        for (let type of SEARCH_TYPES) {
            for (let element of elements) {
                if (!result.has(element)) {
                    let text = getText(element);
                    if (this.isTextContainsSearchQuery(query, text, type)) {
                        result.set(element, {template: element, searchType: type});
                    }
                }
            }
        }
        let array: { searchType: SearchType, template: T }[] = [];
        for (let item of result.values()) {
            array.push(item);
        }
        array.sort((a, b) => {
            if (a.searchType !== b.searchType) {
                return a.searchType === STRICT_SEARCH ? -1 : 1;
            }
            return order ? order(a.template, b.template) : 0;
        });
        let finalResult = array.slice(0, max).map(item => item.template);
        if (reverse) {
            finalResult = finalResult.reverse();
        }
        return finalResult;
    }

    public static searchTemplates(siteId: string, query: string, max: number): OperatorApplicationAutomaticTemplateInterface[] {
        let searchList: OperatorApplicationAutomaticTemplateInterface[] = [];

        for (let key in this.templates) {
            if (this.templates.hasOwnProperty(key)) {
                let template = this.templates[key];
                if (template.siteIds.includes(siteId) || !OperatorApplicationConfigInstance.suggestAutoRememberedTemplatesFromSameSiteOnline) {
                    searchList.push(template);
                }
            }
        }
        return this.search(query, searchList, item => item.search, (a, b) => {
            return a.count > b.count ? -1 : 1;
        }, max);
    }

    public static isTextContainsSearchQuery(query: string, text: string, type: SearchType): boolean {
        if (type === STRICT_SEARCH) {
            return text.includes(query);
        }
        for (let part of query.split(" ")) {
            if (!text.includes(part)) {
                return false;
            }
        }
        return true;
    }

    public static removeAutoSuggestedTemplate(text: string) {
        for (let key in this.templates) {
            if (this.templates.hasOwnProperty(key)) {
                let template = this.templates[key];
                if (template.text == text) {
                    delete this.templates[key];
                }
            }
        }
    }
}

export interface OperatorApplicationAutomaticTemplateInterface {
    search: string;
    text: string;
    count: number;
    siteIds: string[];
}